import { useParams, useHistory } from "react-router-dom";
import { useState, useEffect } from "react";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import { cuteToast } from "../UI/cute";
import "./appconfig.css";

const AppConfig = () => {
  const { project_id } = useParams();
  const history = useHistory();
  const { sendRequest } = useHttp();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [buildingAndroid, setBuildingAndroid] = useState(false);
  const [buildingIos, setBuildingIos] = useState(false);
  const [buildHistory, setBuildHistory] = useState([]);

  useEffect(() => {
    document.title = `${endPoints.title} | App Configuration`;
    loadAppConfig();
    loadBuildHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAppConfig = () => {
    sendRequest(
      { url: `${endPoints.projectAppConfig}/${project_id}/app-config`, method: "GET" },
      (response) => {
        setLoading(false);
        if (response.status === 200) {
          setConfig(response.body);
          if (response.body.android_build_status === "building") {
            setBuildingAndroid(true);
            pollAndroidBuildStatus();
          }
          if (response.body.ios_build_status === "building") {
            setBuildingIos(true);
            pollIosBuildStatus();
          }
        } else {
          setError(response.message || "Failed to load app configuration");
        }
      }
    );
  };

  const loadBuildHistory = () => {
    sendRequest(
      { url: `${endPoints.projectAppConfig}/${project_id}/builds`, method: "GET" },
      (response) => {
        if (response.status === 200) setBuildHistory(response.body || []);
      }
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const updateConfig = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    setSaving(true);
    sendRequest(
      { url: `${endPoints.projectAppConfig}/${project_id}/app-config`, method: "POST", body: config },
      (response) => {
        setSaving(false);
        cuteToast({
          type: response.status === 200 ? "success" : "error",
          title: response.status === 200 ? "Saved" : "Error",
          message: response.status === 200 ? "App configuration saved" : (response.message || "Failed to save"),
          timer: 3000,
        });
      }
    );
  };

  const handleBuildAndroid = () => {
    setBuildingAndroid(true);
    updateConfig("android_build_status", "building");
    sendRequest(
      { url: `${endPoints.projectAppConfig}/${project_id}/build-android`, method: "POST", body: config },
      (response) => {
        if (response.status === 200) {
          cuteToast({ type: "success", title: "Android Build Started", message: "Build may take 5-10 minutes.", timer: 5000 });
          pollAndroidBuildStatus();
        } else {
          setBuildingAndroid(false);
          updateConfig("android_build_status", "failed");
          cuteToast({ type: "error", title: "Build Failed", message: response.message || "Failed to trigger build", timer: 5000 });
        }
      }
    );
  };

  const pollAndroidBuildStatus = () => {
    const interval = setInterval(() => {
      sendRequest(
        { url: `${endPoints.projectAppConfig}/${project_id}/build-status`, method: "GET" },
        (response) => {
          if (response.status === 200) {
            const { android_build_status, android_apk_url } = response.body;
            if (android_build_status === "completed" || android_build_status === "failed") {
              clearInterval(interval);
              setBuildingAndroid(false);
              updateConfig("android_build_status", android_build_status);
              if (android_apk_url) updateConfig("android_apk_url", android_apk_url);
              loadBuildHistory();
              cuteToast({
                type: android_build_status === "completed" ? "success" : "error",
                title: android_build_status === "completed" ? "Build Complete" : "Build Failed",
                message: android_build_status === "completed" ? "APK ready for download" : "Check logs.",
                timer: 5000,
              });
            }
          }
        }
      );
    }, 15000);
    setTimeout(() => { clearInterval(interval); setBuildingAndroid(false); }, 15 * 60 * 1000);
  };

  const handleBuildIos = () => {
    setBuildingIos(true);
    updateConfig("ios_build_status", "building");
    sendRequest(
      { url: `${endPoints.projectAppConfig}/${project_id}/build-ios`, method: "POST", body: config },
      (response) => {
        if (response.status === 200) {
          cuteToast({ type: "success", title: "iOS Build Started", message: "Build may take 5-10 minutes.", timer: 5000 });
          pollIosBuildStatus();
        } else {
          setBuildingIos(false);
          updateConfig("ios_build_status", "failed");
          cuteToast({ type: "error", title: "Build Failed", message: response.message || "Failed to trigger build", timer: 5000 });
        }
      }
    );
  };

  const pollIosBuildStatus = () => {
    const interval = setInterval(() => {
      sendRequest(
        { url: `${endPoints.projectAppConfig}/${project_id}/build-status`, method: "GET" },
        (response) => {
          if (response.status === 200) {
            const { ios_build_status, ios_ipa_url } = response.body;
            if (ios_build_status === "completed" || ios_build_status === "failed") {
              clearInterval(interval);
              setBuildingIos(false);
              updateConfig("ios_build_status", ios_build_status);
              if (ios_ipa_url) updateConfig("ios_ipa_url", ios_ipa_url);
              loadBuildHistory();
              cuteToast({
                type: ios_build_status === "completed" ? "success" : "error",
                title: ios_build_status === "completed" ? "Build Complete" : "Build Failed",
                message: ios_build_status === "completed" ? "IPA ready for download" : "Check logs.",
                timer: 5000,
              });
            }
          }
        }
      );
    }, 15000);
    setTimeout(() => { clearInterval(interval); setBuildingIos(false); }, 15 * 60 * 1000);
  };

  // Loading state
  if (loading) {
    return (
      <div className="apc-center-state">
        <div>
          <div className="apc-spinner" />
          <p>Loading app configuration...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || (!loading && !config)) {
    return (
      <div className="apc-center-state">
        <div>
          <i className="bi bi-exclamation-triangle apc-error-icon"></i>
          <h5>{error || "Failed to load configuration"}</h5>
          <button className="apc-error-back" onClick={() => history.goBack()}>
            <i className="bi bi-arrow-left"></i> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="apc-page">
      <div className="apc-container">
        {/* Header */}
        <div className="apc-header">
          <div className="apc-header-left">
            <button className="apc-back-btn" onClick={() => history.goBack()}>
              <i className="bi bi-arrow-left"></i>
            </button>
            <div className="apc-header-text">
              <h2><i className="bi bi-phone"></i> Mobile App Configuration</h2>
              <p>Configure your app for App Store & Google Play</p>
            </div>
          </div>
          <button className="apc-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? <span className="apc-inline-spinner" /> : <i className="bi bi-check-lg"></i>}
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>

        <div className="apc-grid">
          {/* Left Column — Config Form */}
          <div>
            {/* App Identity */}
            <div className="apc-card">
              <div className="apc-card-header">
                <i className="bi bi-app-indicator"></i>
                <h5>App Identity</h5>
              </div>
              <div className="apc-card-body">
                <div className="apc-field">
                  <label className="apc-field-label">App Name</label>
                  <input
                    type="text"
                    className="apc-field-input"
                    value={config.app_name}
                    onChange={(e) => updateConfig("app_name", e.target.value)}
                    placeholder="My Business App"
                  />
                  <span className="apc-field-hint">Appears under the app icon on home screen</span>
                </div>

                <div className="apc-field">
                  <label className="apc-field-label">Bundle ID</label>
                  <input
                    type="text"
                    className="apc-field-input apc-field-input--mono"
                    value={config.bundle_id}
                    onChange={(e) => updateConfig("bundle_id", e.target.value.toLowerCase())}
                    placeholder="com.company.appname"
                  />
                  <span className="apc-field-hint">Unique identifier (e.g., com.company.appname)</span>
                </div>

                <div className="apc-field">
                  <label className="apc-field-label">App Icon</label>
                  <div
                    className="apc-icon-preview"
                    style={{ backgroundColor: config.icon_url ? "transparent" : (config.splash_bg_color || "#059669") }}
                  >
                    {config.icon_url ? (
                      <img src={config.icon_url} alt="App Icon" />
                    ) : (
                      <span className="apc-icon-initials">
                        {(config.app_name || "A").substring(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    className="apc-field-input apc-field-input--sm"
                    value={config.icon_url}
                    onChange={(e) => updateConfig("icon_url", e.target.value)}
                    placeholder="Icon image URL"
                  />
                  <span className="apc-field-hint">1024x1024px recommended</span>
                </div>
              </div>
            </div>

            {/* Status Bar & Splash */}
            <div className="apc-card">
              <div className="apc-card-header">
                <i className="bi bi-palette"></i>
                <h5>Status Bar & Splash</h5>
              </div>
              <div className="apc-card-body">
                <div className="apc-color-row">
                  <div className="apc-color-field">
                    <label className="apc-field-label">Splash Background</label>
                    <div className="apc-color-picker">
                      <input type="color" value={config.splash_bg_color} onChange={(e) => updateConfig("splash_bg_color", e.target.value)} />
                      <span className="apc-color-hex">{config.splash_bg_color}</span>
                      <button className="apc-color-reset" onClick={() => updateConfig("splash_bg_color", "#059669")} disabled={config.splash_bg_color === "#059669"} title="Reset">
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </div>
                  </div>
                  <div className="apc-color-field">
                    <label className="apc-field-label">Status Bar</label>
                    <div className="apc-color-picker">
                      <input type="color" value={config.status_bar_color} onChange={(e) => updateConfig("status_bar_color", e.target.value)} />
                      <span className="apc-color-hex">{config.status_bar_color}</span>
                      <button className="apc-color-reset" onClick={() => updateConfig("status_bar_color", "#059669")} disabled={config.status_bar_color === "#059669"} title="Reset">
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </div>
                  </div>
                  <div className="apc-color-field">
                    <label className="apc-field-label">Text Color</label>
                    <div className="apc-color-picker">
                      <input type="color" value={config.status_bar_text_color || "#ffffff"} onChange={(e) => updateConfig("status_bar_text_color", e.target.value)} />
                      <span className="apc-color-hex">{config.status_bar_text_color || "#ffffff"}</span>
                      <button className="apc-color-reset" onClick={() => updateConfig("status_bar_text_color", "#ffffff")} disabled={(config.status_bar_text_color || "#ffffff") === "#ffffff"} title="Reset">
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* App Store Info */}
            <div className="apc-card">
              <div className="apc-card-header">
                <i className="bi bi-shop"></i>
                <h5>App Store Information</h5>
              </div>
              <div className="apc-card-body">
                <div className="apc-field">
                  <label className="apc-field-label">Category</label>
                  <select className="apc-field-input" value={config.app_store_category} onChange={(e) => updateConfig("app_store_category", e.target.value)}>
                    <option value="Business">Business</option>
                    <option value="Food & Drink">Food & Drink</option>
                    <option value="Health & Fitness">Health & Fitness</option>
                    <option value="Lifestyle">Lifestyle</option>
                    <option value="Medical">Medical</option>
                    <option value="Utilities">Utilities</option>
                    <option value="Education">Education</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="Shopping">Shopping</option>
                    <option value="Travel">Travel</option>
                  </select>
                </div>
                <div className="apc-field">
                  <label className="apc-field-label">Description</label>
                  <textarea
                    className="apc-field-input"
                    rows={4}
                    value={config.app_store_description}
                    onChange={(e) => updateConfig("app_store_description", e.target.value)}
                    placeholder="Describe your app for the store listing..."
                  />
                  <span className="apc-field-hint">{(config.app_store_description || "").length}/4000 characters</span>
                </div>
                <div className="apc-field">
                  <label className="apc-field-label">Keywords</label>
                  <input
                    type="text"
                    className="apc-field-input"
                    value={config.app_store_keywords}
                    onChange={(e) => updateConfig("app_store_keywords", e.target.value)}
                    placeholder="pizza, delivery, restaurant, italian"
                  />
                  <span className="apc-field-hint">Comma-separated keywords for search</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column — Preview & Build */}
          <div className="apc-right-sticky">
            <p className="apc-preview-label">Splash Preview</p>

            {/* Phone Frame */}
            <div className="apc-phone">
              <div className="apc-phone-notch" />
              <div className="apc-phone-statusbar" style={{ backgroundColor: config.status_bar_color, color: config.status_bar_text_color || "#ffffff" }}>
                <span>9:41</span>
                <span style={{ fontSize: "10px" }}>5G</span>
              </div>
              <div className="apc-phone-splash" style={{ backgroundColor: config.splash_bg_color || "#059669" }}>
                <div className={`apc-splash-icon${config.icon_url ? "" : " apc-splash-icon--placeholder"}`}>
                  {config.icon_url ? (
                    <img src={config.icon_url} alt="" />
                  ) : (
                    <span style={{ fontSize: "24px", fontWeight: 700, color: config.status_bar_text_color || "#fff", letterSpacing: "1px" }}>
                      {(config.app_name || "A").substring(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                <span className="apc-splash-name" style={{ color: config.status_bar_text_color || "#fff" }}>
                  {config.app_name || "App Name"}
                </span>
                <div className="apc-splash-dots">
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="apc-splash-dot" style={{ background: (config.status_bar_text_color || "#fff") + "80", animationDelay: `${i * 0.2}s` }} />
                  ))}
                </div>
              </div>
            </div>

            {/* Build & Download */}
            <div className="apc-card apc-build-card">
              <div className="apc-card-header">
                <i className="bi bi-hammer"></i>
                <h5>Build & Download</h5>
              </div>
              <div className="apc-card-body">
                <p className="apc-build-hint">Save configuration first, then build your app.</p>

                {/* Android */}
                <div className="apc-build-row apc-build-row--android">
                  <div className="apc-build-info">
                    <strong>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#3ddc84"><path d="M17.523 2.386l1.342-2.476a.354.354 0 00-.617-.348L16.88 2.072A8.44 8.44 0 0012 .96a8.44 8.44 0 00-4.88 1.112L5.752-.438a.354.354 0 00-.617.348L6.477 2.386A7.854 7.854 0 002.3 9.235h19.4a7.854 7.854 0 00-4.177-6.849zM7.5 7.125a.938.938 0 110-1.875.938.938 0 010 1.875zm9 0a.938.938 0 110-1.875.938.938 0 010 1.875z"/></svg>
                      Android APK
                    </strong>
                    {config.android_build_status === "completed" && <span className="apc-build-status apc-build-status--ready"><i className="bi bi-check-circle-fill"></i> Ready</span>}
                    {config.android_build_status === "failed" && <span className="apc-build-status apc-build-status--failed"><i className="bi bi-x-circle-fill"></i> Failed</span>}
                  </div>
                  <div className="apc-build-actions">
                    {config.android_apk_url && (
                      <a href={config.android_apk_url} className="apc-build-dl" target="_blank" rel="noopener noreferrer" title="Download APK">
                        <i className="bi bi-download"></i>
                      </a>
                    )}
                    <button className="apc-build-btn apc-build-btn--android" onClick={handleBuildAndroid} disabled={buildingAndroid}>
                      {buildingAndroid ? <><span className="apc-inline-spinner" /> Building...</> : "Build APK"}
                    </button>
                  </div>
                </div>

                {/* iOS */}
                <div className="apc-build-row apc-build-row--ios">
                  <div className="apc-build-info">
                    <strong>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#555"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                      iOS IPA
                    </strong>
                    {config.ios_build_status === "building" && <span className="apc-build-status apc-build-status--building"><span className="apc-inline-spinner" style={{ borderTopColor: "var(--bland-purple-600)", borderColor: "var(--bland-purple-200)", width: 10, height: 10 }} /> Building...</span>}
                    {config.ios_build_status === "completed" && <span className="apc-build-status apc-build-status--ready"><i className="bi bi-check-circle-fill"></i> Ready</span>}
                    {config.ios_build_status === "failed" && <span className="apc-build-status apc-build-status--failed"><i className="bi bi-x-circle-fill"></i> Failed</span>}
                  </div>
                  <div className="apc-build-actions">
                    {config.ios_ipa_url && (
                      <a href={config.ios_ipa_url} className="apc-build-dl" target="_blank" rel="noopener noreferrer" title="Download IPA">
                        <i className="bi bi-download"></i>
                      </a>
                    )}
                    <button className="apc-build-btn apc-build-btn--ios" onClick={handleBuildIos} disabled={buildingIos}>
                      {buildingIos ? <><span className="apc-inline-spinner" /> Building...</> : "Build IPA"}
                    </button>
                  </div>
                </div>

                <p className="apc-build-footnote">iOS builds run on GitHub Actions (macOS). Build time: ~5-10 minutes.</p>
              </div>
            </div>

            {/* Build History */}
            <div className="apc-card apc-history-card">
              <div className="apc-card-header">
                <i className="bi bi-clock-history"></i>
                <h5>Build History</h5>
              </div>
              {buildHistory.length === 0 ? (
                <p className="apc-history-empty">No builds yet. Build your first APK or IPA above.</p>
              ) : (
                <div className="apc-history-list">
                  {buildHistory.map((build) => (
                    <div key={build.id} className="apc-history-item">
                      <div className="apc-history-left">
                        {build.platform === "android" ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="#3ddc84"><path d="M17.523 2.386l1.342-2.476a.354.354 0 00-.617-.348L16.88 2.072A8.44 8.44 0 0012 .96a8.44 8.44 0 00-4.88 1.112L5.752-.438a.354.354 0 00-.617.348L6.477 2.386A7.854 7.854 0 002.3 9.235h19.4a7.854 7.854 0 00-4.177-6.849zM7.5 7.125a.938.938 0 110-1.875.938.938 0 010 1.875zm9 0a.938.938 0 110-1.875.938.938 0 010 1.875z"/></svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#555"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                        )}
                        <div className="apc-history-meta">
                          <div className="apc-history-name">{build.filename || (build.platform === "android" ? "APK Build" : "IPA Build")}</div>
                          <div className="apc-history-date">{formatDate(build.createdAt)} &middot; {formatFileSize(build.file_size)}</div>
                        </div>
                      </div>
                      <div className="apc-history-right">
                        <span className={`apc-history-badge apc-history-badge--${build.status}`}>{build.status}</span>
                        {build.download_url && (
                          <a href={build.download_url} className="apc-build-dl" target="_blank" rel="noopener noreferrer" title="Download">
                            <i className="bi bi-download"></i>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppConfig;
